<script lang="ts" setup>
import { apiUrl } from '@/helpers/api';
import type { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import {
    PlanningPermissionApplicationComment,
    PpaMessageCollection
} from '@/models/PlanningPermissionApplicationComment';
import { onMounted, ref, type Ref } from 'vue';

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

defineExpose({
    loadPpaMessages
});

const loading: Ref<boolean> = ref(false);
const messages: Ref<PlanningPermissionApplicationComment[]> = ref([]);

onMounted(() => {
    loadPpaMessages();
});

async function loadPpaMessages() {
    loading.value = true;
    const ppaMsgCollection = new PpaMessageCollection();
    messages.value = (
        await ppaMsgCollection
            .setQueryParams({
                filter: [{ property: 'planning_permission_application_id', value: props.geschaeft.id }],
                sort: [{ property: 'created', direction: 'desc' }]
            })
            .query()
    ).getModels();

    loading.value = false;
}

function nl2br(str: string | null) {
    if (!str) {
        return '';
    }

    return str.replace(/\r?\n/g, '<br />');
}

function downloadUrl(documentId: number) {
    return apiUrl('/Document/downloadDocument', {
        document_id: documentId
    });
}
</script>

<template>
    <div>
        <div v-if="loading">
            <ktg-message-feed class="mb-1rem">
                <div slot="title">
                    <h5 class="ktg-message-feed-title" slot="title">{{ $t('mitteilungen') }}</h5>
                    <ktg-line class="-mx-1rem mt-1rem mb-1_5rem"></ktg-line>
                </div>
                <ktg-message>
                    <h3 class="ktg-message-title" slot="title">
                        <ktg-skeleton :loading="loading" style="max-width: 50%"></ktg-skeleton>
                    </h3>
                    <ktg-skeleton :loading="loading" style="max-width: 80%"></ktg-skeleton>
                    <ktg-skeleton :loading="loading" style="max-width: 90%"></ktg-skeleton>
                    <ktg-skeleton :loading="loading" style="max-width: 60%"></ktg-skeleton>
                </ktg-message>
            </ktg-message-feed>
        </div>
        <div v-else-if="!messages.length">
            <h5 class="ktg-title-05 ml-7rem mt-2_5rem">{{ $t('mitteilungen') }}</h5>
            <ktg-line class="mt-1rem mb-1_5rem mx-6rem"></ktg-line>
            <p class="ktg-body ml-7rem mb-3_5rem">Es liegen zurzeit keine neuen Mitteilungen vor.</p>
        </div>
        <ktg-message-feed v-else class="mb-1rem">
            <div slot="title">
                <h5 class="ktg-message-feed-title" slot="title">{{ $t('mitteilungen') }}</h5>
                <ktg-line class="-mx-1rem mt-1rem mb-1_5rem"></ktg-line>
            </div>
            <ktg-message
                v-for="(message, idx) in messages"
                :key="message.id!"
                :direction="message.isOwn ? 'sent' : 'received'"
                :unread="idx === 0"
            >
                <h3 class="ktg-message-title" slot="title">{{ message.typeDisplay }}</h3>
                <p
                    class="ktg-message-text"
                    style="overflow-wrap: break-word; word-break: break-word"
                    v-html="nl2br(message.comment || '')"
                ></p>
                <ktg-message-attachment
                    v-for="(documentId, idx) in message.document_ids || []"
                    :key="documentId"
                    :href="downloadUrl(documentId)"
                    :download="message.document_filenames ? message.document_filenames[idx] : '(unknown)'"
                >
                    <div class="filenameDiv" style="overflow-wrap: break-word; word-break: break-word">
                        {{ message.document_filenames ? message.document_filenames[idx] : '(unknown)' }}
                    </div>
                </ktg-message-attachment>
                <ktg-message-meta
                    :icon="message.isOwn ? 'user' : 'home'"
                    :author="message.creator_role === 'gemeinde' ? $t('gemeinde') : message.author"
                    :direction="message.isOwn ? 'sent' : 'received'"
                    :date-time="message.created?.toISOString()"
                ></ktg-message-meta>
            </ktg-message>
        </ktg-message-feed>
    </div>
</template>

<style lang="scss" scoped></style>
